import { useDispatch, useSelector } from 'react-redux';
import { Transition, SwitchTransition } from 'react-transition-group';
import { useLocation } from 'react-router-dom';
import {
  getItem as getItemSelector, getItemsLoading, getItemsLikeLoading,
} from 'store/items/selectors';
import {
  getItem as getItemAction,
  updateItem as updateItemAction,
  deleteItems,
  likeItem as likeItemAction,
} from 'store/items/actions';
import { useEffect, useCallback, useState } from 'react';
import Gallery from 'library/components/gallery';
import LikeButton from 'library/components/like-button';
import SliderField from 'library/components/form/image-slider';
import UploadModal from 'library/components/modal/image-upload';
import { TABS } from 'library/components/form/color-picker/component';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CancelButton, ConfirmButton } from 'library/components/modal/styles';
import {
  createImage, createImageByUrl, resetImage, resetImageByUrl,
} from 'store/images/actions';
import {
  createImageByUrlData,
  createImageByUrlLoading,
  createImageData,
  createImageLoading,
} from 'store/images/selectors';
import { batchAddToProject } from 'store/project/actions';
import useSetList from 'library/hooks/use-deferred-data/use-set-list';
import useCategoryList from 'library/hooks/use-deferred-data/use-category-list';
import { LoaderBox } from '../entity-loader/loader-box';
import {
  Container,
  Icon,
  Header as Header4,
  DetailsContainer,
  StyledForm,
  Actions,
  FormActions,
  ItemActionHorizontal,
  InfoContainer,
} from './styles';
import {
  // ATTRIBUTES_KEYS,
  ACTIONS,
} from './constants';
import PickRadioModal from '../modal/pick-radio';
import AddToContainerEntityModal from '../modal/pick-entity';
import PickAndCreateSet from '../modal/create-and-pick-set';
import ItemFields from './fields';
import SearchByColorModal from '../modal/pick-color';
// eslint-disable-next-line import/no-duplicates
import AddWishlistModal from '../modal/batch-confirm';
// eslint-disable-next-line import/no-duplicates
import DeleteItemsModal from '../modal/batch-confirm';
import { format, ITEM_VALIDATION } from './helpers';
import useProjectList from '../../hooks/use-deferred-data/use-project-list';

const Item = ({ onClose, openedItemId, labelMaps }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isGalleryOpened, setGalleryIsOpened] = useState(false);
  const [isUploadOpened, setUploadOpened] = useState(false);
  const [openedGalleryImageId, setOpenedGalleryImageId] = useState(null);
  const [isPickCategoryModalOpened, togglePickCategoryModal] = useState(false);
  const [isAddToSetModalOpened, toggleAddToSetModal] = useState(false);
  const [isAddToProjectModalOpened, toggleAddToProjectModal] = useState(false);
  const [isSearchByColorModalOpened, toggleSearchByColorModal] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const data = useSelector(getItemSelector);
  const isLoading = useSelector(getItemsLoading);
  const isAdditionalDataLoadDenied = !data || isLoading;
  const isImageLoading = useSelector(createImageLoading);
  const newImageData = useSelector(createImageData);
  const newImageByUrlData = useSelector(createImageByUrlData);
  const isImageByUrlLoading = useSelector(createImageByUrlLoading);
  const isLikeInProgress = useSelector(getItemsLikeLoading);
  const [isAddItemsToWishlistModalOpened, toggleAddItemsToWishlistModal] = useState(false);
  const [isDeleteItemsModalOpened, toggleDeleteItemsModal] = useState(false);

  const getDefaultValues = (d = {}) => ({
    name: d?.name || '',
    tags: d?.tags ? (d?.tags || []).map(({ id }) => ({ value: id })) : null,
    category: d?.category_id || null,
    images: d?.images || [],
    colors: (d?.colors || []).map(({ value }) => `${value}`),
    barcode: d?.barcode || '',
    price: d?.price || '',
    note: d?.note || '',
    sets: (d?.sets || []).map(({ id }) => id),
    manufacturer: d?.manufacturer_id ? [{ value: d.manufacturer_id }] : null,
    storage: d?.storage_id ? [{ value: d.storage_id }] : null,
    count: d?.count || '',
    is_wishlisted: d?.is_wishlisted || false,
  });

  const { options: projectOptions, isLoading: isProjectListLoading, onQueryChange: onProjectQueryChange } = useProjectList(!isAddToProjectModalOpened);
  const { options: categories, onQueryChange: onCategoryQueryChange, labelMap: categoryMap } = useCategoryList({
    skip: false,
    defaultSnapshot: !isAdditionalDataLoadDenied ? {
      ...(data.category_id ? { [data?.category_id]: data.category_name } : {}),
      ...(labelMaps?.categories ? { ...labelMaps.categories } : {}),
    } : undefined,
  });

  const {
    options: setOptions,
    isLoading: isSetListLoading,
    labelMap: setMap,
    onQueryChange: onSetQueryChange,
  } = useSetList(
    !data || isLoading,
    (data?.sets || []).reduce((result, { id, name }) => ({
      ...result,
      [id]: name,
    }), {}),
  );
  const isItemDataLoading = isLoading;

  const methods = useForm({
    resolver: yupResolver(ITEM_VALIDATION),
    mode: 'onChange',
    defaultValues: getDefaultValues(data || undefined),
    shouldUnregister: true,
  });
  const { isValid, dirtyFields, defaultValues } = methods.formState;
  const handleSubmit = useCallback((values) => {
    const formData = format({
      values: {
        ...values,
        ...(methods.getValues('manufacturer')?.toString() === defaultValues.manufacturer?.toString() ? { manufacturer: methods.getValues('manufacturer') } : {}),
        ...(methods.getValues('tags')?.toString() === defaultValues.tags?.toString() ? { tags: methods.getValues('tags') } : {}),
        ...(methods.getValues('storage')?.toString() === defaultValues.storage?.toString() ? { storage: methods.getValues('storage') } : {}),
      },
      dirtyFields: methods.formState.dirtyFields,
      defaultValues: getDefaultValues(data),
    });

    // setTimeout(() => {
    dispatch(updateItemAction({
      id: openedItemId,
      data: formData,
      messages: {
        success: 'Item successfully updated.',
        error: 'We could not add that item',
      },
    }));

    onClose({ });
    // }, 500);
  }, [dirtyFields, defaultValues, openedItemId, data]);

  const handleGalleryClose = () => {
    setGalleryIsOpened(false);
    setOpenedGalleryImageId(null);
  };

  const handleGalleryOpen = (imageId) => {
    setGalleryIsOpened(true);
    setOpenedGalleryImageId(imageId);
  };

  useEffect(() => {
    if (openedItemId) {
      dispatch(getItemAction({ id: openedItemId }));
    }
    // else {
    //   // dispatch(resetItem());
    // }
  }, [openedItemId]);

  useEffect(() => () => {
    if (openedItemId && location.pathname) {
      onClose(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isLoading && data && openedItemId) {
      methods.reset(getDefaultValues(data), {
        keepDefaultValues: false,
        keepValues: false,
      });
    }
  }, [!isLoading && data && openedItemId]);

  const defaultStyle = {
    transition: `opacity ${300}ms ease-in-out`,
    opacity: 0,
  };

  const transitionStyles = {
    entering: { opacity: 1 },
    entered: { opacity: 1 },
    exiting: { opacity: 0 },
    exited: { opacity: 0 },
  };

  const handleUploadModalOpen = () => {
    setUploadOpened(true);
  };
  const handleUploadModalClose = () => {
    setUploadOpened(false);
  };

  const handlePickCategoryModalOpen = () => {
    togglePickCategoryModal(true);
  };
  const handlePickCategoryModalClose = () => {
    togglePickCategoryModal(false);
  };

  const handleAddToSetModalOpen = () => {
    toggleAddToSetModal(true);
  };

  const handleAddToSetModalClose = () => {
    toggleAddToSetModal(false);
  };

  const handleAddToProjectModalClose = () => {
    toggleAddToProjectModal(false);
  };

  const handleAddItemsToWishlistModalOpen = () => {
    toggleAddItemsToWishlistModal(true);
  };
  const handleAddItemsToWishlistModalClose = () => {
    toggleAddItemsToWishlistModal(false);
  };

  const handleDeleteItemsModalOpen = () => {
    toggleDeleteItemsModal(true);
  };
  const handleDeleteItemsModalClose = () => {
    toggleDeleteItemsModal(false);
  };

  const fileValidation = yup.object().shape({
    files: yup.array().of(yup.object().shape({
      id: yup.number(),
    })).min(1),
    images: yup.mixed(),
    urls: yup.array().of(yup.object().shape({
      value: yup.string().url().test(
        'validFormat',
        'Invalid image extension, must be one of: jpg, webp, gif, png.',
        (value) => {
          if (typeof value !== 'string') {
            return false;
          }

          if (value === '') {
            return true;
          }

          return (value.match(/^http[^?]*.(jpg|png|gif|webp)(\?(.*))?$/gmi) !== null);
        },
      ),
    })).min(1),
  });

  const categoryValidation = yup.object().shape({
    category: yup.number().required(),
  });

  const setValidation = yup.object().shape({
    // eslint-disable-next-line react/forbid-prop-types
    sets: yup.array().of(yup.number()).default([]),
  });

  const projectIdValidation = yup.object().shape({
    id: yup.number(),
  });

  const onFileRest = () => {
    dispatch(resetImage());
  };

  const onImageUrlReset = () => {
    dispatch(resetImageByUrl());
  };

  const onFileUpload = (file) => {
    dispatch(createImage({ data: file }));
  };

  const onFileSubmit = ({ files = [] }) => {
    const currentFiles = methods.getValues('images') || [];

    methods.setValue('images', [...currentFiles, ...files.map((file) => ({ ...file, isNew: true }))], { shouldTouch: true, shouldValidate: true, shouldDirty: true });

    handleUploadModalClose();
  };

  const onCategoryAdd = ({ category }) => {
    handlePickCategoryModalClose();
    methods.setValue('category', category, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
  };

  const onAddToProject = ({ id }) => {
    dispatch(batchAddToProject({
      ids: [openedItemId],
      id,
      type: 'ITEM',
      messages: { success: 'Item added to project' },
      resetBrowseItems: true,
    }));

    handleAddToSetModalClose();
  };

  const onAddToSet = ({ sets }) => {
    if (isEditable) {
      methods.setValue('sets', sets, { shouldDirty: true });
    } else {
      dispatch(updateItemAction({
        id: openedItemId,
        messages: { success: 'Item added to set' },
        data: { sets_ids: sets },
      }));
    }

    handleAddToSetModalClose();
  };

  const handleSearchByColorModalOpen = () => {
    toggleSearchByColorModal(true);
  };

  const handleSearchByColorModalClose = () => {
    toggleSearchByColorModal(false);
  };

  const onSearchByColor = ({ colors }) => {
    methods.setValue('colors', colors, { shouldDirty: true });

    toggleSearchByColorModal(false);
  };

  const colorsValidation = yup.object().shape({
    colors: yup.array().of(yup.string().required()).default([]).test(
      'mustDifferFromDefaultValue',
      'Colors value wasn\'t changed',
      (value, { parent }) => {
        return parent.colors.sort().toString() === value.sort().toString();
      },
    ),
  });

  const modalFieldHandlers = {
    handleUploadModalOpen,
    handlePickCategoryModalOpen,
    handleAddToSetModalOpen,
    handleGalleryOpen,
    handleSearchByColorModalOpen,
    handleAddItemsToWishlistModalOpen,
    handleDeleteItemsModalOpen,
  };

  const handleClose = (...values) => {
    if (isEditable) {
      setIsEditable(false);

      methods.reset(getDefaultValues(data), { keepDefaultValues: true });
    } else {
      onClose({ values, isUpdated: true });
    }
  };

  const wishlistSubmit = () => {
    dispatch(updateItemAction({
      id: openedItemId,
      data: { is_wishlisted: !data?.is_wishlisted },
      messages: {
        success: 'Item updated.',
        error: 'We could not add that item',
      },
    }));

    toggleAddItemsToWishlistModal(false);
  };

  const deleteItemsSubmit = () => {
    dispatch(deleteItems({ ids: [data.id] }));
  };

  const actionsMap = {
    pencil: () => {
      setIsEditable(((prevState) => !prevState));
    },
    heart: () => {
      toggleAddItemsToWishlistModal(((prevState) => !prevState));
    },
    like: () => {
      dispatch(likeItemAction({ id: openedItemId }));
    },
    trash: () => {
      toggleDeleteItemsModal(((prevState) => !prevState));
    },
    set: () => {
      toggleAddToSetModal(((prevState) => !prevState));
    },
    projects: () => {
      toggleAddToProjectModal(((prevState) => !prevState));
    },
  };

  // todo - replace with schema-like config
  const renderAction = ({ name, title, disabled }) => {
    let child = (
      <>
        <span>{title}</span>
        <Icon className={(isEditable && name === 'pencil') ? 'icon-close' : `icon-${name}`} />
      </>
    );

    if(name === 'heart') {
      child = (
        <>
          <span>
            {(data?.is_wishlisted) ? 'Remove from wishlist' : 'Add to wishlist'}
            {' '}
          </span>

          <Icon className={(data?.is_wishlisted) ? 'icon-heart-filled' : 'icon-heart'} />
        </>
      );
    } else if (name === 'like') {
      return (
        <LikeButton
          level={data?.like_level}
          key={name}
          disabled={disabled}
          isLoading={isLikeInProgress}
          actionType={name}
          onClick={actionsMap[name]}
        />
      );
    }
    return (
      <ItemActionHorizontal
        key={name}
        disabled={disabled}
        isLoading={isLikeInProgress}
        actionType={name}
        onClick={actionsMap[name]}
      >
        {child}
      </ItemActionHorizontal>
    );
  };

  const onImageUrlUpload = (urlData) => {
    dispatch(createImageByUrl(urlData));
  };

  return (
    <Container top={71} key={`${data?.id}-${methods.formState.submitCount}`}>
      {isGalleryOpened && data && !isEditable && (
        <Gallery
          data={data.images ?? []}
          onClose={handleGalleryClose}
          openedImageId={openedGalleryImageId}
        />
      )}
      <SwitchTransition>
        <Transition
          key={isItemDataLoading ? 'loading' : data?.id}
          in={isItemDataLoading}
          appear={isItemDataLoading}
          timeout={300}
        >
          {(state) => (
            <div
              key={data?.id}
              style={{
                ...defaultStyle,
                ...transitionStyles[state],
              }}
            >
              {isItemDataLoading
                ? <LoaderBox />
                : (
                  <div>
                    <header>
                      <Header4>
                        {isEditable ? 'Edit item details' : data?.name}
                      </Header4>
                      <ItemActionHorizontal onClick={handleClose}>
                        {
                          isEditable ? (
                            <span>Exit without saving</span>
                          ) : (<span>Close item view</span>)
                        }
                        <Icon className="icon-close-1" />
                      </ItemActionHorizontal>
                    </header>
                    <DetailsContainer>
                      <FormProvider {...methods}>
                        <StyledForm
                          onSubmit={methods.handleSubmit(handleSubmit)}
                          onKeyDown={(e) => {
                            if(e.key === 'Enter') {
                              e.preventDefault();
                              methods.trigger().then(() => {
                                setTimeout(() => {
                                  methods.handleSubmit(handleSubmit)();
                                }, 200);
                              });
                            }
                          }}
                        >
                          <SliderField
                            name="images"
                            key="itemImagesSlider"
                            onItemClick={handleGalleryOpen}
                            isEditable={isEditable}
                            defaultValue={methods.getValues('images')}
                          />
                          <InfoContainer>
                            <ItemFields
                              isEditable={isEditable}
                              labelMaps={{
                                setMap, categoryMap,
                              }}
                              data={data}
                              isAdditionalDataLoadDenied={isAdditionalDataLoadDenied}
                              modalFieldHandlers={modalFieldHandlers}
                            />
                            {!isEditable
                            && (
                              <Actions>
                                {ACTIONS.map(renderAction)}
                              </Actions>
                            )}
                          </InfoContainer>
                          { isEditable && (
                            <FormActions>
                              <CancelButton onClick={handleClose}>Cancel</CancelButton>
                              <ConfirmButton
                                disabled={!isValid}
                                type="submit"
                                aria-label="Save changes"
                              >
                                <span>Save changes</span>
                              </ConfirmButton>
                            </FormActions>
                          )}
                        </StyledForm>
                      </FormProvider>
                    </DetailsContainer>
                  </div>
                )}
            </div>
          )}
        </Transition>
      </SwitchTransition>
      <UploadModal
        text=""
        contrastingFooter
        name="files"
        title="Add image"
        submitText="Add image"
        isOpened={isUploadOpened}
        onClose={handleUploadModalClose}
        onReset={onFileRest}
        onUpload={onFileUpload}
        onImageUrlUpload={onImageUrlUpload}
        onResetImageUrl={onImageUrlReset}
        isLoading={isImageLoading || isImageByUrlLoading}
        newImageData={newImageData}
        newImageByUrlData={newImageByUrlData}
        shouldUnregister={false}
        onSubmit={onFileSubmit}
        defaultValues={{
          files: [],
          images: [],
          urls: [{ value: '' }],
        }}
        validation={fileValidation}
        overlayClassName="Overlay"
      />
      <PickRadioModal
        text=""
        name="category"
        title="Pick a category"
        placeholder="Search for a category"
        submitText="Save changes"
        cancelText="Cancel"
        isOpened={isPickCategoryModalOpened}
        onClose={handlePickCategoryModalClose}
        onSubmit={onCategoryAdd}
        validation={categoryValidation}
        overlayClassName="Overlay"
        cancelWidth="94px"
        options={categories}
        defaultValue={methods.getValues('category')}
        onSearch={onCategoryQueryChange}
      />
      <PickAndCreateSet
        isSetList
        isMultipleAllowed
        text=""
        title="Add item to set(s)"
        placeholder="Search for sets"
        submitText="Save changes"
        cancelText="Cancel"
        isOpened={isAddToSetModalOpened}
        isLoading={isSetListLoading}
        onClose={handleAddToSetModalClose}
        onSearch={onSetQueryChange}
        onSubmit={onAddToSet}
        options={setOptions}
        defaultValue={methods.getValues('sets')}
        validation={setValidation}
        overlayClassName="Overlay"
        cancelWidth="94px"
      />
      <AddToContainerEntityModal
        isProjectList
        isMultipleAllowed={false}
        text=""
        name="id"
        title="Add item to project"
        placeholder="Search for projects"
        submitText="Save changes"
        cancelText="Cancel"
        isOpened={isAddToProjectModalOpened}
        isLoading={isProjectListLoading}
        onClose={handleAddToProjectModalClose}
        onSubmit={onAddToProject}
        options={projectOptions}
        validation={projectIdValidation}
        onSearch={onProjectQueryChange}
        defaultValue={undefined}
        overlayClassName="Overlay"
        cancelWidth="94px"
      />
      <SearchByColorModal
        text=""
        isCancelDisplayed={false}
        contrastingFooter
        name="colors"
        title="Edit colors"
        submitText="Submit selected colors"
        isOpened={isSearchByColorModalOpened}
        onClose={handleSearchByColorModalClose}
        onSubmit={onSearchByColor}
        validation={colorsValidation}
        defaultValue={methods.getValues('colors')}
        defaultTab={TABS.PICTURE}
        images={methods.getValues('images')}
        overlayClassName="Overlay"
      />
      <AddWishlistModal
        text={`You are about to ${data?.is_wishlisted ? 'remove' : 'add'} "${data?.name}" ${data?.is_wishlisted ? 'from' : 'to'} wishlist, are you sure?`}
        itemText={data?.name}
        title={`${data?.is_wishlisted ? 'Remove' : 'Add'} item ${data?.is_wishlisted ? 'from' : 'to'} wishlist`}
        submitText="Yes"
        cancelText="Cancel"
        isOpened={isAddItemsToWishlistModalOpened}
        onClose={handleAddItemsToWishlistModalClose}
        onSubmit={wishlistSubmit}
        overlayClassName="Overlay"
      />
      <DeleteItemsModal
        text="You are about to delete selected items, are you sure?"
        itemText={data?.name}
        title="Delete item?"
        submitText="Yes"
        cancelText="Cancel"
        isOpened={isDeleteItemsModalOpened}
        onClose={handleDeleteItemsModalClose}
        onSubmit={deleteItemsSubmit}
        overlayClassName="Overlay"
      />
    </Container>
  );
};

export default Item;
